import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import productApi from '@/lib/productApi';
import { inlineError, type InlineError } from '@/lib/errorReport';
import { getHubSpotContext } from '@/lib/hubspotContext';
import type { BillingPeriod, ProductSpec, ProductWorkbench } from '@/types/product';
import { calculate, parseMoney, priceChanged } from '@/components/products/workbenchPricing';
import { resolveSpecRateKeys } from '../../../backend/src/utils/hourlyRateCatalog';

interface Drafts {
  products: Record<string, { revision: string; spec: ProductSpec; billingPeriod?: BillingPeriod }>;
  rates: Record<string, { previousCents: number | null; value: string }>;
}
const emptyDrafts = (): Drafts => ({ products: {}, rates: {} });
const key = () => `products.workbench.v1.${getHubSpotContext().portalId ?? 'global'}`;
const message = (error: unknown) =>
  (error as { response?: { data?: { error?: string } } }).response?.data?.error ??
  'De wijziging kon niet worden opgeslagen. Probeer opnieuw.';

export function useProductWorkbench() {
  const [data, setData] = useState<ProductWorkbench | null>(null);
  const [drafts, setDrafts] = useState<Drafts>(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(key()) ?? 'null');
      return saved && typeof saved.products === 'object' && typeof saved.rates === 'object'
        ? (saved as Drafts)
        : emptyDrafts();
    } catch {
      return emptyDrafts();
    }
  });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<InlineError | null>(null);
  const [storageError, setStorageError] = useState(false);
  const draftsRef = useRef(drafts);
  const undo = useRef<Drafts[]>([]);
  const redo = useRef<Drafts[]>([]);
  const [historyVersion, setHistoryVersion] = useState(0);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setData(await productApi.workbench());
      setError(null);
    } catch (err) {
      setError(
        inlineError(
          (err as { response?: { data?: { error?: string } } }).response?.data?.error ??
            'Producten konden niet worden geladen. Probeer opnieuw.',
          err
        )
      );
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    void reload();
  }, [reload]);
  const commit = useCallback((next: Drafts) => {
    draftsRef.current = next;
    setDrafts(next);
    try {
      localStorage.setItem(key(), JSON.stringify(next));
      setStorageError(false);
    } catch {
      setStorageError(true);
    }
  }, []);

  const update = useCallback(
    (change: (current: Drafts) => Drafts) => {
      const current = draftsRef.current;
      undo.current = [...undo.current.slice(-49), current];
      redo.current = [];
      commit(change(current));
      setHistoryVersion((version) => version + 1);
      setError(null);
    },
    [commit]
  );

  const changeSpec = useCallback(
    (id: string, spec: ProductSpec) => {
      const product = data?.products.find((item) => item.id === id);
      if (!product) return;
      update((current) => {
        const products = { ...current.products };
        const period = current.products[id]?.billingPeriod;
        if (
          JSON.stringify(spec) === JSON.stringify(product.spec) &&
          (period === undefined || period === product.billingPeriod)
        )
          delete products[id];
        else
          products[id] = {
            ...current.products[id],
            revision: current.products[id]?.revision ?? product.revision,
            spec,
          };
        return { ...current, products };
      });
    },
    [data, update]
  );

  const changeBillingPeriod = (id: string, billingPeriod: BillingPeriod) => {
    const product = data?.products.find((item) => item.id === id);
    if (!product) return;
    update((current) => {
      const products = { ...current.products };
      const spec = products[id]?.spec ?? product.spec;
      if (
        billingPeriod === product.billingPeriod &&
        JSON.stringify(spec) === JSON.stringify(product.spec)
      )
        delete products[id];
      else
        products[id] = {
          revision: products[id]?.revision ?? product.revision,
          spec,
          billingPeriod,
        };
      return { ...current, products };
    });
  };

  const stageProduct = (id: string) => {
    const product = data?.products.find((item) => item.id === id);
    if (product)
      update((current) => ({
        ...current,
        products: { ...current.products, [id]: { revision: product.revision, spec: product.spec } },
      }));
  };

  const changeRates = (changes: Array<{ id: string; value: string }>) => {
    update((current) => {
      const rates = { ...current.rates };
      for (const { id, value } of changes) {
        const rate = data?.rates.find((item) => item.id === id);
        if (!rate) continue;
        if (parseMoney(value) === rate.currentCents) delete rates[id];
        else
          rates[id] = {
            previousCents: current.rates[id] ? current.rates[id].previousCents : rate.currentCents,
            value,
          };
      }
      return { ...current, rates };
    });
  };
  const changeRate = (id: string, value: string) => changeRates([{ id, value }]);

  const conflicts = useMemo(() => {
    if (!data) return [];
    return [
      ...Object.entries(drafts.products)
        .filter(
          ([id, draft]) => data.products.find((p) => p.id === id)?.revision !== draft.revision
        )
        .map(([id]) => data.products.find((p) => p.id === id)?.name ?? 'Een verwijderd product'),
      ...Object.entries(drafts.rates)
        .filter(
          ([id, draft]) => data.rates.find((r) => r.id === id)?.currentCents !== draft.previousCents
        )
        .map(([id]) => data.rates.find((r) => r.id === id)?.name ?? 'Een verwijderd tarief'),
    ];
  }, [data, drafts]);
  const invalidRates = Object.values(drafts.rates).some((rate) => parseMoney(rate.value) === null);
  const ratesByKey = useMemo(() => {
    const rates = { ...data?.ratesByKey };
    for (const [id, draft] of Object.entries(drafts.rates)) {
      const rate = data?.rates.find((item) => item.id === id);
      const amount = parseMoney(draft.value);
      if (rate && amount !== null) rates[rate.key] = amount;
    }
    return rates;
  }, [data, drafts.rates]);
  const products = useMemo(
    () =>
      data?.products.map((product) => {
        const spec = resolveSpecRateKeys(
          product,
          drafts.products[product.id]?.spec ?? product.spec,
          new Set(Object.keys(ratesByKey)),
          data.rateReplacements
        );
        const computation = calculate(spec, ratesByKey, data);
        return {
          ...product,
          billingPeriod: drafts.products[product.id]?.billingPeriod ?? product.billingPeriod,
          spec,
          computation,
          changed: priceChanged(product.computation, computation),
          edited: Boolean(drafts.products[product.id]),
        };
      }) ?? [],
    [data, drafts.products, ratesByKey]
  );
  const hasChanges = Boolean(
    Object.keys(drafts.products).length || Object.keys(drafts.rates).length
  );
  const calculationErrors = products.filter(
    (p) => (p.edited || p.changed) && p.computation.variants.some((v) => v.errors.length)
  );
  const invalidVariantNames = products.filter(
    (product) => product.edited && product.spec.variants.some((variant) => !variant.name.trim())
  );

  const apply = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await productApi.applyWorkbench({
        products: [
          ...Object.entries(drafts.products).map(([id, draft]) => ({ id, ...draft })),
          // Publish exactly the specifications visible in the live preview, including open concepts.
          ...products
            .filter(
              (product) => product.published && product.changed && !drafts.products[product.id]
            )
            .map((product) => ({ id: product.id, revision: product.revision, spec: product.spec })),
        ],
        rates: Object.entries(drafts.rates).map(([id, draft]) => ({
          id,
          amountCents: parseMoney(draft.value)!,
          previousCents: draft.previousCents,
        })),
      });
      commit(emptyDrafts());
      undo.current = [];
      redo.current = [];
      await reload();
      return result;
    } catch (err) {
      setError(inlineError(message(err), err));
      return null;
    } finally {
      setBusy(false);
    }
  };
  const discard = () => update(() => emptyDrafts());
  const publishProduct = async (id: string, reason: string) => {
    const product = data?.products.find((item) => item.id === id);
    if (!product) throw new Error('Product is niet meer beschikbaar. Laad de actuele gegevens.');
    if (Object.keys(draftsRef.current.rates).length)
      throw new Error(
        'Publiceer eerst je tariefwijzigingen voordat je dit product apart publiceert.'
      );
    const draft = draftsRef.current.products[id];
    setBusy(true);
    setError(null);
    try {
      await productApi.applyWorkbench({
        products: [{ id, ...(draft ?? { revision: product.revision, spec: product.spec }) }],
        rates: [],
        reason,
      });
      const next = { ...draftsRef.current, products: { ...draftsRef.current.products } };
      delete next.products[id];
      commit(next);
      undo.current = [];
      redo.current = [];
      setHistoryVersion((version) => version + 1);
      await reload();
    } finally {
      setBusy(false);
    }
  };
  const stepHistory = (direction: 'undo' | 'redo') => {
    const from = direction === 'undo' ? undo : redo;
    const to = direction === 'undo' ? redo : undo;
    const next = from.current.pop();
    if (!next) return;
    to.current.push(drafts);
    commit(next);
    setHistoryVersion(historyVersion + 1);
  };

  // Product details read server drafts. Transfer this product's work before navigating.
  const prepareDetail = async (id: string) => {
    const draft = drafts.products[id];
    if (!draft) return true;
    if (conflicts.length) {
      setError(
        inlineError('Laad eerst de actuele gegevens; dit concept kan intussen gewijzigd zijn.')
      );
      return false;
    }
    setBusy(true);
    try {
      await productApi.saveDraft(id, {
        spec: draft.spec,
        ...(draft.billingPeriod ? { billingPeriod: draft.billingPeriod } : {}),
        expectedRevision: draft.revision,
      });
      const fresh = await productApi.workbench();
      const product = fresh.products.find((item) => item.id === id);
      setData(fresh);
      const next = { ...draftsRef.current, products: { ...draftsRef.current.products } };
      delete next.products[id];
      commit(next);
      // It is now a server concept, available to the detail editor and its publication action.
      return Boolean(product);
    } catch (err) {
      setError(inlineError(message(err), err));
      return false;
    } finally {
      setBusy(false);
    }
  };

  return {
    data,
    products,
    drafts,
    loading,
    busy,
    error,
    storageError,
    reload,
    changeSpec,
    changeBillingPeriod,
    stageProduct,
    changeRate,
    changeRates,
    ratesByKey,
    conflicts,
    invalidRates,
    calculationErrors,
    invalidVariantNames,
    hasChanges,
    apply,
    publishProduct,
    discard,
    prepareDetail,
    canApply:
      Boolean(data) &&
      !loading &&
      hasChanges &&
      !busy &&
      !invalidRates &&
      !conflicts.length &&
      !calculationErrors.length &&
      !invalidVariantNames.length,
    undo: () => stepHistory('undo'),
    redo: () => stepHistory('redo'),
    canUndo: Boolean(undo.current.length),
    canRedo: Boolean(redo.current.length),
  };
}
